import { createStore } from 'react-sweet-state';
import { cloneDeep } from 'lodash';

import { contentTypeFromBriefType } from 'src/data/brief';
import {
  actualizeGenerationTool,
  GENERATION_TOOL,
  GENERATION_TOOL_GROUP,
  getToolBriefType,
  isContentImproverTool,
  isFreestyleGenerationTool,
  isRewriteGenerationTool,
  mapGenerationTool,
} from 'src/data/generationTool';
import { getQueryParamValue } from 'src/lib/browser';
import { ROUTE_PARAMS } from 'src/routes';

import { DISPLAY_MODE, SIDEBAR_GENERIC_ITEM } from '../../const';
import { getCustomMultiPromptGroupQueryParam, getSidebarItemFromGenerationTool } from '../../utils';

const INITIAL_STATE = {
  project: null,
  generationTool: null,
  freestyleToolId: null,

  sidebarSelectedItem: null,
  currentDisplayMode: null,
  popupsQueue: [],
  currentVisiblePopup: null,

  ratingsCount: {},
};

export const PRIVATE_ACTIONS = {
  init:
    () =>
    ({ setState, dispatch }, { project }) => {
      setState({
        ...cloneDeep(INITIAL_STATE),
        project,
      });

      dispatch(PRIVATE_ACTIONS.initGenerationTool());
      dispatch(PRIVATE_ACTIONS.initSidebarSelection());
    },

  update:
    () =>
    ({ setState }, { project }) => {
      if (!project) {
        return;
      }

      setState({ project });
    },

  initGenerationTool:
    () =>
    ({ setState }, { project, generationTool }) => {
      // Selected generation tool might be passed from outside
      const generationToolFromUrl = getQueryParamValue(ROUTE_PARAMS.generationTool);
      const generationToolToUse = generationTool || generationToolFromUrl;
      const actualizedTool = actualizeGenerationTool(generationToolToUse);
      if (actualizedTool) {
        setState({ generationTool: actualizedTool });
        return;
      }

      // Also, generation tool can be read from previous generations
      const lastRequest = project?.generationRequests?.edges[0]?.node;
      const lastUsedTool = actualizeGenerationTool(lastRequest?.generationTool);
      if (lastUsedTool) {
        setState({
          generationTool: isFreestyleGenerationTool(lastUsedTool)
            ? GENERATION_TOOL.freestyle
            : lastUsedTool,
        });
        return;
      }

      // We can always fall back to default value
      setState({
        generationTool: actualizedTool,
      });
    },

  initSidebarSelection:
    () =>
    ({ getState, setState }) => {
      const { generationTool } = getState();
      if (generationTool === GENERATION_TOOL.customMultiPrompt) {
        const customMultiPromptGroup = getCustomMultiPromptGroupQueryParam();
        if (customMultiPromptGroup) {
          return setState({ sidebarSelectedItem: customMultiPromptGroup });
        }
      }
      const sidebarItem = generationTool
        ? getSidebarItemFromGenerationTool(generationTool)
        : SIDEBAR_GENERIC_ITEM.mostPopular;
      setState({ sidebarSelectedItem: sidebarItem });
    },

  alignSidebarSelection:
    () =>
    ({ getState, setState }) => {
      const { generationTool } = getState();
      if (isFreestyleGenerationTool(generationTool)) {
        setState({ sidebarSelectedItem: GENERATION_TOOL_GROUP.anyCopy });
      } else if (isRewriteGenerationTool(generationTool) || isContentImproverTool(generationTool)) {
        setState({ sidebarSelectedItem: GENERATION_TOOL_GROUP.boost });
      }
    },
};

const ACTIONS = {
  getProject:
    () =>
    ({ getState }) => {
      return getState().project;
    },

  getGenerationTool:
    () =>
    ({ getState }) => {
      return getState().generationTool;
    },
  setGenerationTool:
    (generationTool) =>
    ({ setState, dispatch }) => {
      const tool = mapGenerationTool(generationTool);
      setState({ generationTool: tool });

      dispatch(PRIVATE_ACTIONS.alignSidebarSelection());
    },
  setCustomMultiPromptId:
    (id) =>
    ({ setState }) => {
      setState({ customMultiPromptId: id });
    },
  getCustomMultiPromptId:
    () =>
    ({ getState }) => {
      return getState().customMultiPromptId;
    },
  getBriefType:
    () =>
    ({ getState }) => {
      const { generationTool } = getState();
      return getToolBriefType(generationTool);
    },
  getContentType:
    () =>
    ({ dispatch }) => {
      const briefType = dispatch(ACTIONS.getBriefType());
      return contentTypeFromBriefType(briefType);
    },

  activateFreestyleTool:
    (freestyleToolId, generationTool) =>
    ({ setState }) => {
      setState({
        generationTool: generationTool || GENERATION_TOOL.freestyle,
        freestyleToolId,
      });
    },

  getFreestyleToolId:
    () =>
    ({ getState }) => {
      return getState().freestyleToolId;
    },

  getSidebarSelectedItem:
    () =>
    ({ getState }) => {
      return getState().sidebarSelectedItem;
    },
  selectSidebarItem:
    (item) =>
    ({ setState }) => {
      let newTool = null;
      if (item === GENERATION_TOOL_GROUP.anyCopy) {
        newTool = GENERATION_TOOL.freestyle;
      } else if (item === GENERATION_TOOL_GROUP.boost) {
        newTool = GENERATION_TOOL.basicContentImprover;
      } else if (item === GENERATION_TOOL_GROUP.image) {
        newTool = GENERATION_TOOL.imageWithDescription;
      }

      setState({
        sidebarSelectedItem: item,
        generationTool: newTool,
      });
    },

  activateDisplayMode:
    (mode) =>
    ({ setState }) => {
      setState({ currentDisplayMode: mode });
    },

  activateListView:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.activateDisplayMode(DISPLAY_MODE.list));
    },

  activateDocumentView:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.activateDisplayMode(DISPLAY_MODE.document));
    },

  isDocumentViewActive:
    () =>
    ({ getState }) => {
      const { currentDisplayMode } = getState();
      return currentDisplayMode === DISPLAY_MODE.document;
    },

  isListModeActive:
    () =>
    ({ getState }) => {
      const { currentDisplayMode } = getState();
      return currentDisplayMode === DISPLAY_MODE.list;
    },

  getDisplayMode:
    () =>
    ({ getState }) => {
      return getState().currentDisplayMode;
    },

  setRatingsCount:
    (ratingsCount) =>
    ({ setState, getState }) => {
      if (!ratingsCount) {
        return;
      }

      const currentRatingsCount = getState().ratingsCount;
      const updatedAssetRatings = ratingsCount[Object.keys(ratingsCount)[0]];
      // update all tools that have the same asset types
      Object.keys(currentRatingsCount).map((key) => {
        const currentAssetKeys = Object.keys(currentRatingsCount[key]);
        currentAssetKeys.forEach((assetKey) => {
          if (updatedAssetRatings[assetKey]) {
            currentRatingsCount[key][assetKey] = updatedAssetRatings[assetKey];
          }
        });
        return currentRatingsCount;
      });
      setState({ ratingsCount: { ...currentRatingsCount, ...ratingsCount } });
    },
};

export const MainStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'document-view-main',
});
